import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Search, Truck, Phone, Mail, MapPin, FileText, 
  CheckCircle, AlertCircle, Zap, User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickAssignModal = ({ open, onClose, load, onAssigned }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    if (open) {
      fetchStates();
      // Auto-detect state from load pickup address
      if (load?.pickup_address) {
        const match = load.pickup_address.match(/,\s*([A-Z]{2})\s*$/i);
        if (match) {
          setStateFilter(match[1].toUpperCase());
        }
      }
    }
  }, [open, load]);

  useEffect(() => {
    if (open) {
      searchDrivers();
    }
  }, [open, stateFilter, searchQuery]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/drivers/count-by-state`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const searchDrivers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.append('state', stateFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('available_only', 'true');
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/drivers/search?${params.toString()}`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Error searching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAssign = async () => {
    if (!selectedDriver) {
      toast.error('Please select a driver');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(`${API}/loads/${load.id}/quick-assign`, {
        driver_id: selectedDriver.id,
        send_contract: true,
        fee_percentage: 0.07
      });
      
      toast.success(`Load assigned to ${selectedDriver.name}!`);
      
      if (response.data.email_sent) {
        toast.success('Contract & load details sent to driver\'s email');
      }
      
      onAssigned(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign load');
    } finally {
      setAssigning(false);
    }
  };

  if (!load) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col glass-card">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Quick Assign Load
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 mt-4">
          {/* Load Summary */}
          <div className="col-span-1 bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Load Details</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-400">Pickup:</span>
                <p className="text-white font-medium">{load.pickup_address}</p>
              </div>
              <div>
                <span className="text-gray-400">Delivery:</span>
                <p className="text-white font-medium">{load.delivery_address}</p>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-700">
                <span className="text-gray-400">Rate:</span>
                <span className="text-green-400 font-bold">${load.rate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Weight:</span>
                <span className="text-white">{load.weight} lbs</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Distance:</span>
                <span className="text-white">{load.distance_miles} mi</span>
              </div>
            </div>

            {selectedDriver && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <h4 className="text-sm font-semibold text-yellow-400 mb-2">Selected Driver</h4>
                <p className="text-white font-medium">{selectedDriver.name}</p>
                <p className="text-gray-400 text-xs">{selectedDriver.phone}</p>
                {!selectedDriver.has_contract && (
                  <div className="flex items-center gap-1 mt-2 text-amber-400 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    Contract will be sent
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Driver Search */}
          <div className="col-span-2 flex flex-col">
            <div className="flex gap-2 mb-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, MC#, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-600"
                />
              </div>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-600 text-white text-sm min-w-[140px]"
              >
                <option value="">All States</option>
                {states.map((s) => (
                  <option key={s.state} value={s.state}>
                    {s.state} ({s.count?.toLocaleString()})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[400px] space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : drivers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Truck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No drivers found</p>
                  <p className="text-xs">Try a different state or search term</p>
                </div>
              ) : (
                drivers.map((driver) => (
                  <div
                    key={driver.id}
                    onClick={() => setSelectedDriver(driver)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedDriver?.id === driver.id
                        ? 'bg-blue-600/30 ring-2 ring-blue-500'
                        : 'bg-slate-800/30 hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-white truncate">{driver.name}</span>
                          {driver.has_contract && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">
                              Contracted
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          {driver.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {driver.phone}
                            </span>
                          )}
                          {driver.home_base && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {driver.home_base}
                            </span>
                          )}
                        </div>
                        {driver.mc_number && (
                          <div className="text-xs text-gray-500 mt-1">
                            MC# {driver.mc_number}
                          </div>
                        )}
                      </div>
                      {selectedDriver?.id === driver.id && (
                        <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-700 flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleQuickAssign}
                disabled={!selectedDriver || assigning}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {assigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Assign + Send Contract
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAssignModal;
